/**
 * Group combination hooks using TanStack Query (#1579 part 3).
 * Mutations invalidate the combinations and the coverage, because a combination
 * changes the slot coverage of its groups.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useLingui } from '@lingui/react';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { useModuleGate } from '@/hooks/useAccess';
import { GROUP_COMBINATION_READ_MODULES } from '@/lib/modules';
import { GroupCombinationsService } from '@/api/services/GroupCombinationsService';
import type {
  CombinationSuggestionList,
  GroupCombination,
  GroupCombinationRequest,
  UpdateGroupCombinationRequest,
} from '@/types/groupCombinations';
import { coverageKeys } from './useShiftCoverage';

export const groupCombinationKeys = {
  all: ['groupCombinations'] as const,
  list: (from: string, to: string) =>
    [...groupCombinationKeys.all, 'list', { from, to }] as const,
  suggestions: (childcareId: string, from: string | null) =>
    [...groupCombinationKeys.all, 'suggestions', { childcareId, from }] as const,
};

const retryUnlessAuthError = (failureCount: number, error: unknown) => {
  if (
    error instanceof Error &&
    (error.message.includes('401') ||
      error.message.includes('403') ||
      error.message.includes('Unauthorized'))
  ) {
    return false;
  }
  return failureCount < 3;
};

/**
 * Fetches the combinations whose validity intersects [from, to] (at most 62 days).
 */
export function useGroupCombinations(
  from: string,
  to: string,
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canReadCombinations = useModuleGate(GROUP_COMBINATION_READ_MODULES);

  return useQuery({
    queryKey: groupCombinationKeys.list(from, to),
    queryFn: async (): Promise<GroupCombination[]> => {
      const response = await GroupCombinationsService.getApiV1GroupCombinations(from, to);
      return response.combinations;
    },
    enabled:
      (options?.enabled ?? true) && !!from && !!to && isSignedIn && isAuthReady && canReadCombinations,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessAuthError,
  });
}

/**
 * Fetches the combination suggestions of one childcare for the 4 weeks from `from`
 * (#1579 part 4, admins). `from` defaults to the next Monday on the server. Read-only;
 * the planner enables it only while the suggestions panel is open. Saving a combination
 * invalidates it, because suggestions that overlap a combination are left out.
 */
export function useCombinationSuggestions(
  childcareId: string | null,
  from?: string,
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: groupCombinationKeys.suggestions(childcareId ?? '', from ?? null),
    queryFn: (): Promise<CombinationSuggestionList> =>
      GroupCombinationsService.getApiV1GroupCombinationsSuggestions(childcareId ?? '', from),
    enabled: (options?.enabled ?? true) && !!childcareId && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessAuthError,
  });
}

function useInvalidateCombinations() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: groupCombinationKeys.all });
    queryClient.invalidateQueries({ queryKey: coverageKeys.all });
  };
}

/** Creates a combination. Errors are shown by the dialog, not by a toast. */
export function useCreateGroupCombination() {
  const { i18n } = useLingui();
  const invalidate = useInvalidateCombinations();

  return useMutation({
    mutationFn: (request: GroupCombinationRequest) =>
      GroupCombinationsService.postApiV1GroupCombinations(request),
    onSuccess: () => {
      invalidate();
      toast.success(
        i18n._({ id: 'shifts.combination.toast.created', message: 'Groups combined' })
      );
    },
  });
}

/** Updates a combination with optimistic locking. */
export function useUpdateGroupCombination() {
  const { i18n } = useLingui();
  const invalidate = useInvalidateCombinations();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateGroupCombinationRequest }) =>
      GroupCombinationsService.putApiV1GroupCombinations(id, request),
    onSuccess: () => {
      invalidate();
      toast.success(
        i18n._({ id: 'shifts.combination.toast.updated', message: 'Combination updated' })
      );
    },
  });
}

/** Deletes a combination. */
export function useDeleteGroupCombination() {
  const { i18n } = useLingui();
  const invalidate = useInvalidateCombinations();

  return useMutation({
    mutationFn: (id: string) => GroupCombinationsService.deleteApiV1GroupCombinations(id),
    onSuccess: () => {
      invalidate();
      toast.success(
        i18n._({ id: 'shifts.combination.toast.deleted', message: 'Combination deleted' })
      );
    },
  });
}
